import { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AppBarButtonGroup } from '@m/alchemy-ui/AppBarButtonGroup';
import { AppMenuButton } from '@m/alchemy-ui/AppMenuButton';
import { AppMenuList } from '@m/alchemy-ui/AppMenuList';
import { AppMenuListExpandableItem } from '@m/alchemy-ui/AppMenuListExpandableItem';
import { AppMenuListItem } from '@m/alchemy-ui/AppMenuListItem';
import { ButtonGroup } from '@m/alchemy-ui/ButtonGroup';
import { HelpIcon } from '@m/alchemy-ui/icons/HelpIcon';
import { LanguageIcon } from '@m/alchemy-ui/icons/LanguageIcon';
import { LogoutIcon } from '@m/alchemy-ui/icons/LogoutIcon';
import { MoreVerticalIcon } from '@m/alchemy-ui/icons/MoreVerticalIcon';
import { SettingsIcon } from '@m/alchemy-ui/icons/SettingsIcon';
import { TeamIcon } from '@m/alchemy-ui/icons/TeamIcon';
import { ZeroStateAddIcon } from '@m/alchemy-ui/icons/ZeroStateAddIcon';
import medalliaAppSwitcher from '@m/app-switcher';
import { Link, useLocation, useNavigate } from 'react-location';
import {
  useInvitesDialog,
  useIsTablet,
  useLanguageOverride,
  useLanguageSwitcher,
  usePostIdeaDialog,
  useRegisterDialog,
  useSignInDialog,
  useUserCreatedChallengeForm,
  useValidationDialog
} from '../../../hooks';
import { CommunityContext, UserContext, useRuntimeCommunity, useRuntimeConfig, useRuntimeUser } from '../../../context';
import { MobileMenu, SearchButton } from '../components';
import { type RouteLink } from '../../../common/interfaces/Route';
import * as Styled from '../Navigation.styled';
import { PageType } from '../../../common/enums';
import { getSocialMediaOptions, SocialMediaMenu } from './SocialMediaMenu';

declare global {
  interface Window {
    userNotValidated?: boolean;
  }
}

export const languageIsoMatch = (l1: string, l2: string) =>
  l1.toLowerCase().replace(/[^a-zA-Z]/g, '') === l2.toLowerCase().replace(/[^a-zA-Z]/g, '');

export const UserNavigation = ({
  links,
  isMobile = false,
  isLoggedIn,
  hasAdminRights = false
}: {
  links?: RouteLink[];
  isMobile?: boolean;
  isLoggedIn: boolean;
  hasAdminRights: boolean;
}) => {
  const intl = useIntl();
  const { canInvite, permissions, displayName, avatar } = useRuntimeUser();
  const user = useContext(UserContext);
  const { loadDialog: loadSignInDialog } = useSignInDialog();
  const { loadDialog: loadRegisterDialog } = useRegisterDialog();
  const { loadDialog: loadPostIdeaDialog } = usePostIdeaDialog();
  const { loadDialog: loadValidationDialog } = useValidationDialog();
  const { loadDialog: loadUserCreatedChallengeForm } = useUserCreatedChallengeForm();
  const { loadDialog: loadInvitesDialog } = useInvitesDialog();
  const canModerateIdeas = permissions?.isApprovalModerator || permissions?.isModerator;
  const canCreateChallenge = permissions?.isChallengeCreator;
  const languageOverride = useLanguageOverride();
  const { device } = useRuntimeConfig();
  const community = useContext(CommunityContext);
  const switchLanguage = useLanguageSwitcher();
  const isDesktop = device === 'desktop';
  const isAppSwitcherOn = (community?.options?.appSwitcher?.length || 0) > 0;
  const isTablet = useIsTablet();
  const navigate = useNavigate();
  const location = useLocation();
  const initialNav = window.location.pathname === '/' ? links?.[0]?.url : window.location.pathname;
  const [mainNavIsActive, setMainNavIsActive] = useState<string | undefined>(initialNav);
  const isUserNotValidated = Boolean(
    user?.isMailValidated === false || (typeof window !== 'undefined' && window.userNotValidated)
  );

  const {
    colors: { userNavBg }
  } = useRuntimeCommunity();

  const socialMediaOptionsMemo = useMemo(() => getSocialMediaOptions(community), [community]);

  const pathname = location.current.pathname;
  useEffect(() => {
    const linkUrls = links?.map((l) => l.url) ?? [];
    if (linkUrls.includes(pathname)) {
      setMainNavIsActive(pathname);
    } else if (pathname === '/' && links?.length) {
      setMainNavIsActive(links?.[0]?.url);
    } else {
      setMainNavIsActive(undefined);
    }
  }, [pathname, links]);

  useEffect(() => {
    const initAppSwitcher = async () => {
      const opts = {
        iconSizePx: 32,
        skipLogin: true,
        headerText: 'Select Application',
        themeOption: userNavBg?.toLowerCase() === 'dark' ? 'light' : 'dark',
        staticLinks: community?.options?.appSwitcher
      };
      // @ts-expect-error - AppSwitcher doesn't export the type
      await medalliaAppSwitcher(opts);
    };

    isDesktop && isAppSwitcherOn && initAppSwitcher();
  }, [isAppSwitcherOn, community, isDesktop, userNavBg]);

  const mobileMenuButtonActions = useMemo((): {
    label: string;
    onClick: () => Promise<void>;
  }[] => {
    const items = [
      {
        label: intl.formatMessage({ id: 'postAnIdea', defaultMessage: 'Post an Idea' }),
        onClick: async () => {
          if (isUserNotValidated) {
            loadValidationDialog();
            return;
          }
          loadPostIdeaDialog();
        }
      }
    ];

    if (canModerateIdeas) {
      items.push({
        label: intl.formatMessage({ id: 'moderateIdeas', defaultMessage: 'Moderate Ideas' }),
        onClick: async () => {
          window.location.href = '/dashboard';
        }
      });
    }

    if (canCreateChallenge) {
      items.push({
        label: intl.formatMessage({ id: 'createAChallenge', defaultMessage: 'Create a Challenge' }),
        onClick: async () => {
          loadUserCreatedChallengeForm();
        }
      });
    }

    socialMediaOptionsMemo.length > 0 &&
      items.push(
        ...socialMediaOptionsMemo.map(({ label, url }) => ({
          label,
          onClick: async () => {
            window.open(url, '_blank');
          }
        }))
      );

    return items;
  }, [canModerateIdeas, canCreateChallenge, loadUserCreatedChallengeForm]); // eslint-disable-line

  return (
    // Workaround for the mobile menu becuase of this https://jira.medallia.com/browse/DES-4157
    // Thread: https://medallia.slack.com/archives/C98CWBBPU/p1716565895464159
    // We can remove the mobilemenu after update Alchemy to 40.6.0 or higher
    <>
      {isMobile ? (
        <MobileMenu isLoggedIn={isLoggedIn} links={links} />
      ) : (
        <Styled.AppNav
          items={links?.map(({ linkTitle, newWindow, type, url }, index) => ({
            label: linkTitle || ' ',
            isActive: mainNavIsActive === url,
            id: linkTitle,
            href: '#',
            onClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
              e.preventDefault();
              const notFirstElement = index !== 0;
              if (type === PageType.URL && notFirstElement) {
                window.open(url, newWindow ? '_blank' : '_self');
              } else if (type === PageType.SURVEY) {
                window.open(url, '_self');
              } else {
                setMainNavIsActive(url);
                navigate({ to: notFirstElement ? url : '/', replace: true });
              }
            }
          }))}
          stringMap={{
            navList: 'Main Menu',
            mobileMenuButtonTooltip: 'This opens the mobile menu',
            mobileMenuIcon: 'open menu',
            overflowIconButton: 'toggle menu'
          }}
        />
      )}
      <AppBarButtonGroup>
        <SearchButton />
        {isMobile || isTablet ? (
          isLoggedIn ? (
            <>
              <Styled.AppNavItem key="login" data-testid="appNavItemProfile">
                <Link
                  to="user/ideastream"
                  data-testid="profile-link"
                  onClick={() => setMainNavIsActive('/user/ideastream')}
                  aria-label={intl.formatMessage({ id: 'profile', defaultMessage: 'Profile' })}
                >
                  {avatar ? (
                    <Styled.NavProfileImage src={avatar} alt={displayName} data-testid="UserAvatar" />
                  ) : (
                    <Styled.UserIconGradient palette="interactiveNavigational" aria-hidden="true" />
                  )}
                </Link>
                <Styled.AppNavSeparator />
              </Styled.AppNavItem>
              {mobileMenuButtonActions.length > 0 && (
                <Styled.MenuButton data-testid="Actions" priority="tertiary" actions={mobileMenuButtonActions}>
                  {intl.formatMessage({ id: 'navActions', defaultMessage: 'Actions' })}
                </Styled.MenuButton>
              )}
            </>
          ) : (
            <>
              <Styled.AppNavButton priority="secondary" onClick={loadRegisterDialog}>
                <FormattedMessage id="register" defaultMessage="Register" />
              </Styled.AppNavButton>
              <Styled.AppNavButton onClick={loadSignInDialog} alignLeft>
                <FormattedMessage id="signIn" defaultMessage="Sign In" />
              </Styled.AppNavButton>
            </>
          )
        ) : (
          <ButtonGroup>
            <SocialMediaMenu socialMediaOptions={socialMediaOptionsMemo} />
            {isLoggedIn ? (
              <>
                <Styled.AppNavButton
                  onClick={() => {
                    if (isUserNotValidated) {
                      loadValidationDialog();
                      return;
                    }
                    loadPostIdeaDialog();
                  }}
                >
                  <ZeroStateAddIcon size={16} aria-hidden="true" />
                  {languageOverride(
                    'postAnIdea',
                    intl.formatMessage({ id: 'postAnIdea', defaultMessage: 'Post an Idea' })
                  )}
                </Styled.AppNavButton>
                <ButtonGroup isJoined>
                  {canModerateIdeas && (
                    <Styled.AppNavButton priority="secondary" href="/dashboard">
                      {intl.formatMessage({ id: 'moderateIdeas', defaultMessage: 'Moderate Ideas' })}
                    </Styled.AppNavButton>
                  )}
                  {canCreateChallenge && (
                    <Styled.AppNavButton priority="secondary" onClick={loadUserCreatedChallengeForm}>
                      {intl.formatMessage({ id: 'createAChallenge', defaultMessage: 'Create a Challenge' })}
                    </Styled.AppNavButton>
                  )}
                </ButtonGroup>

                {isLoggedIn && (
                  <Styled.AppNavItem key="login" data-testid="appNavItemProfile">
                    <Link
                      to="user/ideastream"
                      data-testid="profile-link"
                      onClick={() => setMainNavIsActive('/user/ideastream')}
                      aria-label={intl.formatMessage({ id: 'profile', defaultMessage: 'Profile' })}
                    >
                      {avatar ? (
                        <Styled.NavProfileImage src={avatar} alt={displayName} data-testid="UserAvatar" />
                      ) : (
                        <Styled.UserIconGradient palette="interactiveNavigational" aria-hidden="true" />
                      )}
                    </Link>
                    <Styled.AppNavSeparator />
                  </Styled.AppNavItem>
                )}
              </>
            ) : (
              <>
                <Styled.AppNavButton priority="secondary" onClick={loadRegisterDialog}>
                  <FormattedMessage id="register" defaultMessage="Register" />
                </Styled.AppNavButton>
                <Styled.AppNavButton onClick={loadSignInDialog}>
                  <FormattedMessage id="signIn" defaultMessage="Sign In" />
                </Styled.AppNavButton>
              </>
            )}
          </ButtonGroup>
        )}
        {isLoggedIn && isAppSwitcherOn && isDesktop && (
          <Styled.AppSwitcherWrapper>
            <div id="m_app_switcher" data-testid="m_app_switcher_nav" />
          </Styled.AppSwitcherWrapper>
        )}
        <Styled.ButtonWrapper>
          <AppMenuButton
            label={intl.formatMessage({ id: 'more', defaultMessage: 'More' })}
            shouldHideLabel
            icon={<MoreVerticalIcon size={24} aria-hidden="true" />}
            overlay={() => (
              <AppMenuList>
                <AppMenuListItem icon={<HelpIcon size={16} aria-hidden="true" />}>
                  <a href="https://docs.medallia.com/en/?resourceId=idea-intro" target="_blank">
                    <FormattedMessage id="help" defaultMessage="Help" />
                  </a>
                </AppMenuListItem>
                {isLoggedIn && hasAdminRights && (
                  <AppMenuListItem hasDivider icon={<SettingsIcon size={16} aria-hidden="true" />}>
                    <a href="/category/admin_view">
                      <FormattedMessage id="adminSetup" defaultMessage="Admin Setup" />
                    </a>
                  </AppMenuListItem>
                )}
                {isLoggedIn && canInvite && (
                  <AppMenuListItem hasDivider icon={<TeamIcon size={16} aria-hidden="true" />}>
                    <a
                      href="/"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        loadInvitesDialog();
                      }}
                    >
                      <FormattedMessage id="Invite Users" defaultMessage="Invite Users" />
                    </a>
                  </AppMenuListItem>
                )}
                {community?.options?.allowLanguageChoice && (
                  <AppMenuListExpandableItem
                    label={intl.formatMessage({ id: 'languages', defaultMessage: 'Languages' })}
                    icon={<LanguageIcon size={16} aria-hidden="true" />}
                  >
                    <AppMenuList>
                      {(community?.options?.allowedLanguages || []).map(({ name, iso }) => (
                        <AppMenuListItem key={iso} isActive={languageIsoMatch(intl.locale, iso)}>
                          <a href="/" onClick={(e) => switchLanguage(iso, e)}>
                            {name}
                          </a>
                        </AppMenuListItem>
                      ))}
                    </AppMenuList>
                  </AppMenuListExpandableItem>
                )}
                {isLoggedIn && (
                  <AppMenuListItem hasDivider icon={<LogoutIcon size={16} aria-hidden="true" />}>
                    <a href="/user/logout">
                      <FormattedMessage id="logOut" defaultMessage="Log Out" />
                    </a>
                  </AppMenuListItem>
                )}
              </AppMenuList>
            )}
          />
        </Styled.ButtonWrapper>
      </AppBarButtonGroup>
    </>
  );
};
